import { withSacredImages } from "../utils/image-urls";

/** @type {import('../utils/types').DeityEntry[]} */
const DEITIES_RAW = [
  {
    id: "ganesha",
    name: "Gaṇeśa",
    beginner: {
      identity: "Lord of beginnings, remover of obstacles, son of Śiva and Pārvatī.",
      meaning: "Gaṇeśa clears inner and outer blocks so dharma can flow.",
      why_it_matters: "No auspicious work begins without honoring Gaṇeśa — humility before effort.",
      key_mantra: "ॐ गं गणपतये नमः",
      short_story: "Pārvatī fashioned him from turmeric; Śiva restored his head with an elephant form and made him foremost among gods.",
      simple_sadhana: "Morning: three deep breaths, offer a flower or light, chant the mantra 108 times with steady gaze on his image.",
    },
    deep: {
      tattva: "Buddhi (discriminating wisdom) and vighna-nāśana — he governs the threshold between intention and action.",
      iconography: ["Elephant head — memory and majesty", "Modaka — sweet fruit of realized knowledge", "Mouse vehicle — mind must be small under wisdom"],
      stories: ["Birth from Pārvatī's mālā", "Race around the world — parents as universe", "Scribe of the Mahābhārata"],
      mantras: ["ॐ गं गणपतये नमः", "वक्रतुण्ड महाकाय सूर्यकोटि समप्रभ", "गणानां त्वा गणपतिं हवामहे"],
      stotras: ["Gaṇeśa Atharvaśīrṣa", "Sankata Nashana Gaṇeśa Stotram"],
      puja_vidhi: ["Invoke with dhyāna of red or golden form", "Offer durva grass, red flowers, modaka", "Perform āratī, prostrate, distribute prasāda"],
      sadhana_guide: ["21-day vighna-hara japa at dawn", "Study one chapter of wisdom text after pūjā", "Sevā: help someone begin a good task"],
      do_and_dont: ["Do: begin study and travel with his name", "Don't: skip inner preparation while rushing outward success"],
      charity: ["Feed students", "Support arts and education for children"],
      temples: ["Kanipākam (AP)", "Siddhi Vināyaka (Mumbai)", "Moregaon (Maharashtra)"],
      scriptural_refs: ["Gaṇeśa Purāṇa", "Mudgala Purāṇa", "Gaṇapati Upaniṣad"],
      astrological_notes: ["Associated with Ketu; worship on Wednesdays and Chaturthi days"],
    },
  },
  {
    id: "shiva",
    name: "Śiva",
    beginner: {
      identity: "The auspicious one — destroyer of ignorance, lord of yoga and grace.",
      meaning: "Śiva is pure consciousness wearing the garland of time.",
      why_it_matters: "He teaches that what must fall away for truth to remain — tapas, surrender, stillness.",
      key_mantra: "ॐ नमः शिवाय",
      short_story: "He drank hālāhala poison to save creation; the moon and Gaṅgā rest on his matted locks.",
      simple_sadhana: "Evening: sit quietly, chant Pañcākṣarī 11 or 108 times, offer water or bilva if available.",
    },
    deep: {
      tattva: "Śivatattva — cit (consciousness) and ānanda beyond attribute; Naṭarāja dances creation-destruction.",
      iconography: ["Third eye — fire of knowledge", "Trident — triads transcended", "Crescent moon — time mastered", "Ash — impermanence embraced"],
      stories: ["Dakṣa's sacrifice and Sati", "Marriage with Pārvatī", "Kailāsa as abode of sages", "Markaṇḍeya protected"],
      mantras: ["ॐ नमः शिवाय", "महामृत्युञ्जय मन्त्र", "शिवाय नमस्तुभ्यं"],
      stotras: ["Śiva Mahimna Stotram", "Lingāṣṭakam", "Rudram"],
      puja_vidhi: ["Abhiṣeka with water, milk, honey on liṅga", "Bilva leaves with three petals", "Monday fast optional with night japa"],
      sadhana_guide: ["Pañcākṣarī with breath sync", "Pradoṣa and Mahāśivarātri vigil", "Mauna (silence) one hour weekly"],
      do_and_dont: ["Do: practice honesty and restraint", "Don't: use Śiva only for fear without love"],
      charity: ["Support cremation grounds upkeep", "Feed ascetics and yoga students"],
      temples: ["Kāśī Viśvanāth", "Arunācaleśvara", "Srisailam", "Chidambaram"],
      scriptural_refs: ["Śiva Purāṇa", "Liṅga Purāṇa", "Śvetāśvatara Upaniṣad"],
      astrological_notes: ["Ruled by Saturn; Monday, Pradoṣa, Śivarātri nights"],
    },
  },
  {
    id: "parvati",
    name: "Pārvatī",
    beginner: {
      identity: "Daughter of the mountain, mother of the universe, śakti of Śiva.",
      meaning: "She is the power that makes the formless take form — love, nurture, and fierce protection.",
      why_it_matters: "Honoring the Divine Mother balances strength with compassion in household and heart.",
      key_mantra: "ॐ दुं दुर्गायै नमः",
      short_story: "She performed severe tapas to win Śiva; as Durgā she restored cosmic order from chaos.",
      simple_sadhana: "Friday or Tuesday: light lamp, offer kumkum and flower, chant her name with gratitude to mothers.",
    },
    deep: {
      tattva: "Prakṛti-śakti — icchā, jñāna, kriyā śaktis united in one Mother.",
      iconography: ["Trident and lotus", "Lion or tiger vehicle — courage", "Green/red garments — fertility and auspiciousness"],
      stories: ["Pārvatī's penance", "Annapūrṇā feeding the world", "Kāmākhyā as yoni-pīṭha"],
      mantras: ["ॐ दुं दुर्गायै नमः", "सर्वमङ्गल माङ्गल्ये", "या देवी सर्वभूतेषु"],
      stotras: ["Lalitā Sahasranāma (when initiated)", "Durgā Saptaśatī", "Annapūrṇa Stotram"],
      puja_vidhi: ["Kumkum archana", "Offer cooked food after main deity pūjā", "Kumārī pūjā on Navarātri"],
      sadhana_guide: ["Navarātri nine-night discipline", "Anna-dāna on Fridays", "Chant Durgā Saptaśatī with teacher guidance"],
      do_and_dont: ["Do: respect women and elders", "Don't: invoke fierce forms without purity of intent"],
      charity: ["Feed girls and mothers", "Support women's shelters"],
      temples: ["Māta Māllamma (Telangana)", "Meenakshi (Madurai)", "Kāmākhyā"],
      scriptural_refs: ["Devī Māhātmyam", "Devī Bhāgavata", "Lalitā traditions"],
      astrological_notes: ["Moon and Rahu remedies; Friday worship"],
    },
  },
  {
    id: "vishnu",
    name: "Viṣṇu",
    beginner: {
      identity: "The all-pervading protector who preserves dharma through avatāras.",
      meaning: "Viṣṇu is the steady hand that keeps the cosmic order from dissolving.",
      why_it_matters: "He reminds the seeker that righteousness and refuge are never abandoned.",
      key_mantra: "ॐ नमो नारायणाय",
      short_story: "He measured the universe in three steps as Vāmana; he rests on Ādiśeṣa in yogic sleep between ages.",
      simple_sadhana: "Dawn: visualize blue form on serpent couch, chant Nārāyaṇa mantra, offer tulsi if available.",
    },
    deep: {
      tattva: "Sattva-preserving principle; Nārāyaṇa = refuge of all naras (beings).",
      iconography: ["Conch — sound of dharma", "Discus — time's wheel", "Mace — authority", "Lotus — unfolding consciousness"],
      stories: ["Kūrma churning the ocean", "Nṛsiṃha protecting Prahlāda", "Kṛṣṇa līlā", "Rāma's exile and return"],
      mantras: ["ॐ नमो नारायणाय", "ॐ नमो भगवते वासुदेवाय", "हरिः ॐ"],
      stotras: ["Viṣṇu Sahasranāma", "Nārāyaṇa Kavacam", "Purusha Sūkta"],
      puja_vidhi: ["Tulsi offering essential", "Satyanārāyaṇa vrat on full moon", "Ekādaśī fasting with Viṣṇu focus"],
      sadhana_guide: ["Daily Viṣṇu Sahasranāma", "Ekādaśī discipline", "Study Itihāsa with bhakti"],
      do_and_dont: ["Do: protect the weak in small daily acts", "Don't: perform pūjā without śuddhi (purity)"],
      charity: ["Annadāna", "Support scripture distribution"],
      temples: ["Tirupati", "Sri Rangam", "Puri Jagannāth"],
      scriptural_refs: ["Viṣṇu Purāṇa", "Bhāgavata", "Nārāyaṇīya"],
      astrological_notes: ["Jupiter remedies; Thursday, Ekādaśī, Vaikuṇṭha Ekādaśī"],
    },
  },
  {
    id: "lakshmi",
    name: "Lakṣmī",
    beginner: {
      identity: "Goddess of auspiciousness, wealth, and gracious abundance.",
      meaning: "True śrī is inner purity that attracts outer harmony.",
      why_it_matters: "She teaches that prosperity serves dharma, not ego.",
      key_mantra: "ॐ श्रीं महालक्ष्म्यै नमः",
      short_story: "She arose from the churning ocean on a lotus; she chose Viṣṇu as lord and walks with him.",
      simple_sadhana: "Friday: clean altar, lamp with ghee, chant Śrī mantra, offer sweets with gratitude.",
    },
    deep: {
      tattva: "Icchā-śakti manifest as beauty, generosity, and ethical wealth.",
      iconography: ["Lotus — purity in mud", "Gold coins — circulation of good", "Elephants — royal abundance"],
      stories: ["Samudra manthan", "Eight forms (Aṣṭa Lakṣmī)", "Alakshmi's departure through cleanliness"],
      mantras: ["ॐ श्रीं महालक्ष्म्यै नमः", "कमलासन स्तुति", "श्री सूक्त"],
      stotras: ["Kanakadhārā Stotram", "Śrī Sūkta", "Lakṣmī Aṣṭottaram"],
      puja_vidhi: ["Kumkum and turmeric", "Offer coins symbolically to charity", "Deepāvalī night main worship"],
      sadhana_guide: ["Friday vrata with honesty in business", "Gratitude journal before sleep", "Dāna on Fridays"],
      do_and_dont: ["Do: keep home and accounts clean", "Don't: hoard while neglecting duty"],
      charity: ["Feed on Fridays", "Support livelihood training"],
      temples: ["Kolhapur", "Bhadrachalam Lakṣmī association"],
      scriptural_refs: ["Śrī Sūkta", "Padma Purāṇa", "Lakṣmī Tantra traditions"],
      astrological_notes: ["Venus; Friday, Deepāvalī, Varalakṣmī"],
    },
  },
  {
    id: "saraswati",
    name: "Sarasvatī",
    beginner: {
      identity: "Goddess of learning, speech, music, and refined intellect.",
      meaning: "She flows like a river — knowledge must move, not stagnate in pride.",
      why_it_matters: "Students and artists invoke her so skill serves truth.",
      key_mantra: "ॐ ऐं सरस्वत्यै नमः",
      short_story: "She dwells where speech is pure; Brahmā sought her to create with ordered sound.",
      simple_sadhana: "Study before dawn or dusk: offer white flower, chant Aiṃ, read one śloka with focus.",
    },
    deep: {
      tattva: "Jñāna-śakti — vāk (speech) as creative power aligned with ṛta (cosmic order).",
      iconography: ["Veena — harmony", "White garments — sattva", "Swan — discernment of milk from water"],
      stories: ["Curse and blessing of speech", "Competition with Lakṣmī", "Vasant Pañcamī emergence"],
      mantras: ["ॐ ऐं सरस्वत्यै नमः", "या कुन्देन्दु तुषारहार धवला"],
      stotras: ["Sarasvatī Stotram", "Sarasvatī Vandana"],
      puja_vidhi: ["White flowers, books on altar", "Akṣarābhyāsa for children", "Vasant Pañcamī yellow-white mix"],
      sadhana_guide: ["Daily reading + meditation on meaning", "Music or chanting practice", "Teach one thing freely"],
      do_and_dont: ["Do: speak truth gently", "Don't: use learning to humiliate"],
      charity: ["Donate books", "Support schools"],
      temples: ["Basara (Telangana)", "Kashmir Sharada Peeth traditions"],
      scriptural_refs: ["Rig Veda Sarasvatī hymns", "Devi Upaniṣad"],
      astrological_notes: ["Mercury; Thursday for students, Vasant Pañcamī"],
    },
  },
  {
    id: "rama",
    name: "Śrī Rāma",
    beginner: {
      identity: "Ideal king and son of Daśaratha — maryādā puruṣottama.",
      meaning: "Rāma is dharma walking as a human life.",
      why_it_matters: "His story trains the heart in duty, patience, and righteous grief.",
      key_mantra: "श्री राम जय राम जय जय राम",
      short_story: "Exile, Sītā's abduction, alliance with Hanumān, return to Ayodhyā on Vijayādaśamī.",
      simple_sadhana: "Read or listen to one Rāmāyaṇa episode daily; chant Rāma nāma at dusk.",
    },
    deep: {
      tattva: "Viṣṇu's sattvic avatāra demonstrating sthita-prajñā under suffering.",
      iconography: ["Bow — protection of dharma", "Brothers — saṅga and loyalty", "Forest exile — detachment"],
      stories: ["Ahalyā's release", "Bharata's paduka", "Vibhīṣaṇa's surrender"],
      mantras: ["श्री राम जय राम", "ॐ आपदामपहर्तारम्", "Rāma Rakṣā Stotra"],
      stotras: ["Rāma Rakṣā Stotram", "Adhyātma Rāmāyaṇa selections"],
      puja_vidhi: ["Rāma Navamī midday pūjā", "Sītā-Rāma together on altar", "Vijayādaśamī victory worship"],
      sadhana_guide: ["Saptāha Rāmāyaṇa pārāyaṇa", "Nāma japa with Hanumān", "Act as just elder in family"],
      do_and_dont: ["Do: keep promises", "Don't: justify cruelty as dharma"],
      charity: ["Feed on Navamī and Daśamī", "Support orphans"],
      temples: ["Bhadrachalam", "Ayodhyā", "Rāmeśvaram"],
      scriptural_refs: ["Vālmīki Rāmāyaṇa", "Adhyātma Rāmāyaṇa", "Rāmacaritamānasa"],
      astrological_notes: ["Sun; Rāma Navamī, Vijayādaśamī"],
    },
  },
  {
    id: "krishna",
    name: "Kṛṣṇa",
    beginner: {
      identity: "Divine flute-bearer — child of Devakī, friend of Arjuna, lord of līlā.",
      meaning: "Kṛṣṇa reveals that God is intimacy as well as infinity.",
      why_it_matters: "Bhakti ripens through play, trust, and the Gītā's clarity.",
      key_mantra: "ॐ क्लीं कृष्णाय गोविन्दाय गोपीजन वल्लभाय स्वाहा",
      short_story: "He lifted Govardhana, danced ras līlā, spoke the Bhagavad Gītā on Kurukshetra.",
      simple_sadhana: "Offer butter or fruit, chant name, recall one Gītā verse and its daily application.",
    },
    deep: {
      tattva: "Pūrṇa avatāra — paradox of human charm and cosmic vibhūti.",
      iconography: ["Flute — prāṇa harmony", "Peacock feather — beauty", "Yellow cloth — knowledge"],
      stories: ["Kamsa's end", "Sudāmā's rice", "Uddhava's teaching", "Rukmiṇī Kalyāṇam"],
      mantras: ["ॐ नमो भगवते वासुदेवाय", "Hare Kṛṣṇa mahā-mantra", "Gopāla mantra"],
      stotras: ["Kṛṣṇa Aṣṭottaram", "Madhurashtakam", "Bhagavad Gītā chapters"],
      puja_vidhi: ["Janmāṣṭamī midnight abhiṣeka", "Offer tulsi, butter, milk sweets", "Gītā pārāyaṇa on Ekādaśī"],
      sadhana_guide: ["Rāga-bhakti kīrtan", "Gītā chapter-a-week study", "Sevā in community kitchen"],
      do_and_dont: ["Do: see God in friends", "Don't: imitate līlā without adhikāra"],
      charity: ["Annadāna", "Cow protection where ethical"],
      temples: ["Dwaraka", "Guruvayur", "Udupi"],
      scriptural_refs: ["Bhāgavata Purāṇa", "Bhagavad Gītā", "Hari Vaṃśa"],
      astrological_notes: ["Moon; Janmāṣṭamī, Ekādaśī"],
    },
  },
  {
    id: "hanuman",
    name: "Hanumān",
    beginner: {
      identity: "Son of Vāyu, supreme devotee of Rāma, embodiment of service.",
      meaning: "Strength without ego — power yoked to nāma.",
      why_it_matters: "He grants courage to those who chant Rāma with humility.",
      key_mantra: "ॐ हनुमते नमः",
      short_story: "He leapt across the ocean, carried the mountain of herbs, burned Laṅkā yet remained humble.",
      simple_sadhana: "Tuesday/Saturday: chant Hanuman Chalisa or mantra, offer sindūr if tradition allows.",
    },
    deep: {
      tattva: "Buddhi + śakti in brahmacharya — chastity as focused force.",
      iconography: ["Mace — discipline", "Mountain — burden for others", "Open chest showing Rāma-Sītā"],
      stories: ["Sūrya as guru", "Bharat's ring", "Saving Lakṣmaṇa"],
      mantras: ["ॐ हनुमते नमः", "आञ्जनेयाय विद्महे", "मारुतात्मजाय धीमहि"],
      stotras: ["Hanuman Chalisa", "Sankat Mochan", "Hanuman Aṣṭottaram"],
      puja_vidhi: ["Offer sindūr, oil, garland", "Read Rāmāyaṇa Sundara Kāṇḍa", "Avoid alcohol on vow days"],
      sadhana_guide: ["Physical sādhana + japa", "Serve elders", "Read Sundara Kāṇḍa weekly"],
      do_and_dont: ["Do: speak less, act more", "Don't: use Hanumān only for fear"],
      charity: ["Feed on Saturdays", "Blood donation where able"],
      temples: ["Jakhu", "Panchamukhi traditions", "Prasanthi area Hanumān shrines"],
      scriptural_refs: ["Sundara Kāṇḍa", "Hanumān Chalisa tradition"],
      astrological_notes: ["Mars/Saturn remedies; Tuesday, Saturday"],
    },
  },
  {
    id: "narasimha",
    name: "Narasiṃha",
    beginner: {
      identity: "Man-lion avatāra who protects the devotee beyond all doors.",
      meaning: "God appears where logic ends — for those who call with single-pointed faith.",
      why_it_matters: "Prahlāda's story heals fear of tyranny and injustice.",
      key_mantra: "ॐ उग्रं वीरं महाविष्णुं ज्वलन्तं सर्वतोमुखम्",
      short_story: "Hiraṇyakaśipu could not be killed indoors or out; Narasiṃha emerged from a pillar at twilight.",
      simple_sadhana: "Listen to Prahlāda story; chant Narasiṃha mantra at dusk with lamp.",
    },
    deep: {
      tattva: "Ugra and śānta forms — divine anger is love refusing to abandon the devotee.",
      iconography: ["Lap of father — God as protector", "Claws — precision", "Garland of intestines — ego destroyed"],
      stories: ["Prahlāda's school", "Hiraṇyakaśipu's boons", "Śānta Narasiṃha cooling"],
      mantras: ["Narasiṃha anuṣṭubh", "Ugra Narasiṃha bija", "Prahlāda stuti"],
      stotras: ["Narasiṃha Kavacam", "Prahlāda stuti in Bhāgavata"],
      puja_vidhi: ["Jayantī on Vaiśākha śukla chaturdaśī", "Offer lemon, pepper avoided per local custom", "Evening sandhyā timing"],
      sadhana_guide: ["Courage meditation for oppressed", "Study Bhāgavata Canto 7", "Protect children from abuse"],
      do_and_dont: ["Do: stand for the innocent", "Don't: invoke ugra forms casually"],
      charity: ["Support legal aid for vulnerable"],
      temples: ["Ahobilam", "Mangalagiri", "Penusila"],
      scriptural_refs: ["Bhāgavata 7th canto", "Narasiṃha Purāṇa"],
      astrological_notes: ["Rahu/Ketu; Jayantī, twilight sandhyā"],
    },
  },
  {
    id: "subrahmanya",
    name: "Subrahmaṇya",
    beginner: {
      identity: "Skanda — general of the gods, son of Śiva, lord of tejas.",
      meaning: "He cuts through confusion and grants clarity to warriors and students.",
      why_it_matters: "Worshipped widely in South India for health, marriage, and victory over inner foes.",
      key_mantra: "ॐ सरवण भवाय नमः",
      short_story: "Born from Śiva's tejas, raised by Kṛttikās, slew Tārakāsura and led the devas.",
      simple_sadhana: "Subrahmaṇya ṣaṣṭi or Tuesday: offer milk, chant mantra, visit hill temple if possible.",
    },
    deep: {
      tattva: "Kumāra — eternal youth as undivided awareness; Guha — hidden in cave of heart.",
      iconography: ["Vel (spear) — jñāna śakti", "Peacock — beauty over serpent of desire", "Six faces — six chakras"],
      stories: ["Gaṇeśa race", "Murugan and Valli-Devasena", "Surapadma's defeat"],
      mantras: ["ॐ सरवण भवाय", "षण्मुख प्रity", "Kanda ṣaṣṭi kavacam"],
      stotras: ["Kanda Ṣaṣṭi Kavacam", "Subrahmaṇya Bhujangam"],
      puja_vidhi: ["Vel archana", "Panchamritam on ṣaṣṭi", "Kavadi vow with guru permission"],
      sadhana_guide: ["Early morning hill climb", "Study Tamil/Telugu Skanda stotras", "Discipline in speech"],
      do_and_dont: ["Do: keep vows once taken", "Don't: perform kavadi without preparation"],
      charity: ["Feed on ṣaṣṭi", "Support youth education"],
      temples: ["Tiruttani", "Palani", "Subrahmanya (Karnataka)"],
      scriptural_refs: ["Skanda Purāṇa", "Kanda Purāṇam"],
      astrological_notes: ["Mars; ṣaṣṭi, Tuesdays"],
    },
  },
  {
    id: "durga",
    name: "Durgā",
    beginner: {
      identity: "She who protects the cosmos — Mahiṣāsura's slayer, mother of the gods.",
      meaning: "Durgā means fortress — the Self defended when surrender is complete.",
      why_it_matters: "Navarātri renews the seeker's vow against inner demons.",
      key_mantra: "ॐ दुं दुर्गायै नमः",
      short_story: "Gods united their śaktis; she fought nine nights and restored peace on the tenth day.",
      simple_sadhana: "During Navarātri: lamp, Devi stuti, avoid tamasic food if fasting.",
    },
    deep: {
      tattva: "Chit-śakti as warrior — fearlessness is love without attachment to body.",
      iconography: ["Many arms — multifaceted skill", "Lion — dharma riding desire", "Trident — tri-guṇa transcended"],
      stories: ["Mahiṣāsura mardini", "Durgā Saptaśatī battles", "Kātyāyanī vrata"],
      mantras: ["ॐ ऐं ह्रीं क्लीं चामुण्डायै विच्चे", "Durgā mantras from Saptaśatī"],
      stotras: ["Durgā Saptaśatī", "Argalā Stotram", "Devi Kavacam"],
      puja_vidhi: ["Ghata sthāpana", "Kumārī pūjā", "Ayudha pūjā on Vijayādaśamī"],
      sadhana_guide: ["Nine-night japa with teacher", "Fasting per health", "Night vigil on Ashtamī"],
      do_and_dont: ["Do: respect feminine divine", "Don't: treat Devi as mere ritual"],
      charity: ["Annadāna each night", "Clothes for girls"],
      temples: ["Vijayawada Kanaka Durgā", "Kāmākhyā", "Kolhapur"],
      scriptural_refs: ["Markandeya Purāṇa", "Devi Māhātmyam"],
      astrological_notes: ["Rahu; Navarātri, Vijayādaśamī"],
    },
  },
  {
    id: "kali",
    name: "Kālī",
    beginner: {
      identity: "Dark mother — time, transformation, and fearless compassion.",
      meaning: "She destroys not life but the illusion that binds souls.",
      why_it_matters: "For mature bhaktas she teaches ego-death and urgent compassion.",
      key_mantra: "ॐ क्रीं कालिकायै नमः",
      short_story: "She danced on Śiva to stop destruction of worlds; Rāmakṛṣṇa saw her as living mother.",
      simple_sadhana: "Only with guru lineage: simple lamp, Mahākāli prayer from heart — beginners stay with Durgā form.",
    },
    deep: {
      tattva: "Mahākāla's śakti — kāla as grace when fear of death is surrendered.",
      iconography: ["Garland of heads — ego-selves", "Sword — cutting bondage", "Tongue — intensity of truth"],
      stories: ["Rākṣasa sacrifice", "Śiva under foot", "Ramakrishna's vision"],
      mantras: ["ॐ क्रीं कालिकायै नमः", "Mahākāli stotram — with initiation"],
      stotras: ["Kālī Kavacam", "Mahākāli Stotram"],
      puja_vidhi: ["Tantric paddhati requires dikṣā", "Public Kālī pūjā simplified in Bengal tradition"],
      sadhana_guide: ["Must have qualified guru", "Service to dying and poor", "Strict yama-niyama"],
      do_and_dont: ["Do: humility", "Don't: practice ugra tantra without adhikāra"],
      charity: ["Support hospice", "Feed on Amāvasyā with guidance"],
      temples: ["Kalighat", "Dakshineswar"],
      scriptural_refs: ["Kālī Tantra", "Devī Māhātmyam finale"],
      astrological_notes: ["Saturn/Rahu deep work; Amāvasyā in Kālī traditions"],
    },
  },
  {
    id: "ayyappa",
    name: "Ayyappa",
    beginner: {
      identity: "Hariharaputra — union of Viṣṇu and Śiva, lord of Śabarimala.",
      meaning: "He embodies discipline, equality, and pilgrimage as purification.",
      why_it_matters: "Mandalam vrata trains body and mind for 41 days before hill journey.",
      key_mantra: "स्वാമिये शरणम् अय्यप्प",
      short_story: "Found as infant on the Pampa; revealed as divine prince who ended Mahiṣi.",
      simple_sadhana: "During mandalam: vegetarian diet, celibacy, daily 'Swamiye Saranam', wear mālā if vowed.",
    },
    deep: {
      tattva: "Hari-Hara saṅgama — bhakti and tapas as one path for all castes united.",
      iconography: ["Bell on waist", "Bow and arrow", "Tiger or horse per tradition"],
      stories: ["Mahiṣi marriage boon", "Vāvar Swami friendship", "Erumeli petta thullal"],
      mantras: ["Swamiye Saranam Ayyappa", "Harivarasanam"],
      stotras: ["Harivarasanam", "Ayyappa Pancharatnam"],
      puja_vidhi: ["Mālā dharana from guru", "Irumudi kettu", "Pathinettu padi climb"],
      sadhana_guide: ["41-day vrata rules strictly", "Brahmacharya", "Serve all pilgrims as equals"],
      do_and_dont: ["Do: respect forest and Pampa river", "Don't: break vrata midway without prāyaścitta"],
      charity: ["Annadāna at Erumeli", "Help elderly pilgrims"],
      temples: ["Śabarimala", "Ayyappa temples across Kerala/AP"],
      scriptural_refs: ["Local sthala purāṇas", "Harivarasanam tradition"],
      astrological_notes: ["Makara vilakku season; Mandalam Nov–Jan"],
    },
  },
];

export const DEITIES = withSacredImages("deities", DEITIES_RAW);

export function getDeity(id) {
  return DEITIES.find((d) => d.id === id) ?? null;
}
